import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useLanguage } from "../context/LanguageContext";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  const navLinks = [
    { name: t("nav_home"), path: "/" },
    { name: t("nav_about"), path: "/about" },
    { name: t("nav_products"), path: "/products" },
    { name: t("nav_exports"), path: "/exports" },
    { name: t("nav_certifications"), path: "/certifications" },
    { name: t("nav_blog"), path: "/blog" },
    { name: t("nav_contact"), path: "/contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-brand-bone/80 border-b border-brand-gold/20 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="brand-logo" className="flex items-center">
            <img
              src="/images/fabcrops_logo_transparent.png"
              alt="Fab Crops by P.S.IMPEX"
              className="h-16 w-auto object-contain"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-sm font-sans font-medium tracking-wide transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-brand-gold"
                    : scrolled
                      ? "text-brand-text hover:text-brand-gold"
                      : "text-brand-gold/80 hover:text-brand-gold"
                }`}
              >
                {link.name}
              </Link>
            ))}
            <a
              href="tel:+919872227812"
              data-testid="nav-phone"
              className="flex items-center gap-2 px-5 py-2.5 bg-brand-green text-brand-bone text-sm font-sans font-medium rounded-full hover:bg-brand-gold transition-colors duration-300"
            >
              <Phone size={14} />
              {t("nav_call")}
            </a>
            <LanguageSwitcher />
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-brand-green p-2"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-brand-bone/95 backdrop-blur-xl border-t border-brand-gold/20"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                  className={`text-lg font-serif font-medium ${
                    location.pathname === link.path
                      ? "text-brand-gold"
                      : "text-brand-text"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="tel:+919872227812"
                className="flex items-center justify-center gap-2 px-5 py-3 bg-brand-green text-brand-bone text-sm font-sans font-medium rounded-full mt-2"
              >
                <Phone size={14} />
                +91-9872227812
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
